
import Vue from 'vue';
import Component from 'vue-class-component';
import { BoPanel } from 'bonomo';
import CreateButton from '@/components/shared/CreateButton.vue';
import Datim from '@/components/shared/Datim.vue';
import PaginationBar from '@/components/shared/PaginationBar.vue';
import IntegrationStatusPill from '@/components/settings/identityIntegrations/IntegrationStatusPill.vue';
import { BetterDataTableHeader } from '@/model/betterVuetifyInterfaces';
import { EPermissionActions } from '@/model/EPermissionActions';
import { collectErrors, errorMessage, fieldErrorMessages, generalErrorMessage } from '@/components/settings/identityIntegrations/integrationErrors';
import { protocolLabel } from '@/components/settings/identityIntegrations/protocolLabel';
import {
  IApiFieldError,
  identityIntegrationsService,
  IIdentityIntegration,
  IDENTITY_INTEGRATION_PROTOCOL_OIDC,
  IDENTITY_INTEGRATION_PROTOCOL_SAML,
  IDENTITY_INTEGRATION_STATUS_ACTIVE,
  IDENTITY_INTEGRATION_STATUS_INACTIVE,
} from '@/services/identityIntegrationsService';

const SEARCH_DEBOUNCE_MS = 350;
const WIDE_LIST_MIN_WIDTH = 1400;

interface ICredentialState {
  tone: 'ok' | 'warn' | 'error';
  icon: string;
  label: string;
}
const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

@Component({
  name: 'IdentityIntegrations',
  components: { BoPanel, CreateButton, Datim, PaginationBar, IntegrationStatusPill },
})
export default class IdentityIntegrations extends Vue {
  protected loading: boolean = true;
  protected listError: string | null = null;
  protected integrations: IIdentityIntegration[] = [];
  protected currentPage: number = 1;
  protected total: number = 0;
  protected selectedPerPage: number = 15;
  readonly perPageOptions: number[] = [15, 25, 50];

  protected search: string | null = null;
  protected status: string | null = null;
  protected protocol: string | null = null;
  private searchTimer: any = null;

  protected createDialog: boolean = false;
  protected createFormValid: boolean = false;
  protected creating: boolean = false;
  protected createErrors: IApiFieldError[] = [];
  protected newName: string = '';
  protected newSlug: string = '';
  protected newProtocol: string = IDENTITY_INTEGRATION_PROTOCOL_SAML;

  get canManage(): boolean {
    return this.$canAction(EPermissionActions.ACTION_IDENTITY_INTEGRATIONS_MANAGE);
  }

  get lastPage(): number {
    return Math.max(1, Math.ceil(this.total / (this.selectedPerPage || 15)));
  }

  get hasActiveFilters(): boolean {
    return !!(this.search || this.status || this.protocol);
  }

  /**
   * A stable colour per integration, derived from the slug rather than the row
   * index: the same partner keeps the same monogram colour across pages and
   * filters, which is what makes it useful for scanning at all.
   */
  monogram(integration: IIdentityIntegration): string {
    return (integration.name || integration.slug || '?').trim().charAt(0).toUpperCase();
  }

  monogramStyle(integration: IIdentityIntegration): object {
    const slug = integration.slug || '';
    let hash = 0;

    for (let i = 0; i < slug.length; i++) {
      hash = (hash * 31 + slug.charCodeAt(i)) % 360;
    }

    return {
      backgroundColor: 'hsl(' + hash + ', 45%, 92%)',
      color: 'hsl(' + hash + ', 45%, 32%)',
    };
  }

  clearFilters() {
    this.search = null;
    this.status = null;
    this.protocol = null;
    this.reload();
  }

  get statusOptions() {
    return [
      { text: this.$t('identityIntegrations.status.active') as string, value: IDENTITY_INTEGRATION_STATUS_ACTIVE },
      { text: this.$t('identityIntegrations.status.inactive') as string, value: IDENTITY_INTEGRATION_STATUS_INACTIVE },
    ];
  }

  protected readonly oidcProtocol = IDENTITY_INTEGRATION_PROTOCOL_OIDC;

  get protocolOptions() {
    return [IDENTITY_INTEGRATION_PROTOCOL_SAML, IDENTITY_INTEGRATION_PROTOCOL_OIDC].map((value) => ({
      text: this.protocolLabel(value),
      value,
    }));
  }

  protocolLabel(protocol: string): string {
    return protocolLabel(this, protocol);
  }

  // An OIDC Integration's issuer is in its provider configuration.
  issuerOf(item: IIdentityIntegration): string | null {
    if (item.protocol === IDENTITY_INTEGRATION_PROTOCOL_OIDC) {
      return item.oidc ? item.oidc.issuer : null;
    }

    return item.issuer;
  }

  get nameRules() {
    return [(v: string) => !!v || (this.$t('common.required') as string)];
  }

  get slugRules() {
    return [
      (v: string) => !!v || (this.$t('common.required') as string),
      (v: string) => !v || SLUG_PATTERN.test(v) || (this.$t('identityIntegrations.fields.slugInvalid') as string),
    ];
  }

  get headers(): BetterDataTableHeader[] {
    const headers: BetterDataTableHeader[] = [
      { text: this.$t('identityIntegrations.list.integration') as string, value: 'name', sortable: false },
      { text: this.$t('identityIntegrations.list.provider') as string, value: 'provider', sortable: false },
      { text: this.$t('identityIntegrations.fields.status') as string, value: 'status', sortable: false },
      { text: this.$t('identityIntegrations.list.credential') as string, value: 'credential', sortable: false },
      { text: this.$t('identityIntegrations.fields.communities') as string, value: 'community_ids', sortable: false },
      { text: this.$t('identityIntegrations.list.linkedIdentities') as string, value: 'linked_identity_count', sortable: false, align: 'end' },
    ];

    // The last edit date is the column least needed to pick a row, so it is the
    // one that gives way when the table would otherwise scroll sideways.
    if (this.$vuetify.breakpoint.width >= WIDE_LIST_MIN_WIDTH) {
      headers.push({ text: this.$t('common.updatedAt') as string, value: 'updated_at', sortable: false });
    }

    return headers;
  }

  async loadData() {
    await this.fetchPage(this.currentPage);
  }

  async fetchPage(page: number) {
    this.loading = true;
    this.listError = null;
    try {
      const result = await identityIntegrationsService.list({
        page,
        perPage: this.selectedPerPage,
        status: this.status,
        protocol: this.protocol,
        search: this.search,
      });
      this.integrations = result.data;
      this.currentPage = result.page;
      this.total = result.total;
      this.selectedPerPage = result.per_page;
    } catch (error) {
      // Not "no integrations yet": an empty table after a failed request
      // would say something false.
      this.integrations = [];
      this.listError = errorMessage(this, collectErrors(error)[0]);
    } finally {
      this.loading = false;
    }
  }

  reload() {
    this.fetchPage(1);
  }

  onSearchInput() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    this.searchTimer = setTimeout(() => this.reload(), SEARCH_DEBOUNCE_MS);
  }

  onPageChange(page: number) {
    this.fetchPage(page);
  }

  onPerPageChange(perPage: number) {
    this.selectedPerPage = perPage;
    this.fetchPage(1);
  }

  openDetail(item: IIdentityIntegration) {
    this.$router.push({ name: 'identityIntegration', params: { integrationId: String(item.id) } });
  }

  credentialOf(item: IIdentityIntegration): ICredentialState {
    if (item.protocol === IDENTITY_INTEGRATION_PROTOCOL_OIDC) {
      if (!item.oidc) {
        return { tone: 'warn', icon: 'mdi-key-outline', label: this.$t('identityIntegrations.list.providerMissing') as string };
      }

      return item.oidc.client_secret_configured
        ? { tone: 'ok', icon: 'mdi-key', label: this.$t('identityIntegrations.oidc.secretConfigured') as string }
        : { tone: 'warn', icon: 'mdi-key-outline', label: this.$t('identityIntegrations.oidc.secretMissing') as string };
    }

    if (!item.certificate.configured) {
      return { tone: 'warn', icon: 'mdi-certificate-outline', label: this.$t('identityIntegrations.certificate.missing') as string };
    }

    return item.certificate.usable
      ? { tone: 'ok', icon: 'mdi-certificate', label: this.$t('identityIntegrations.certificate.installed') as string }
      : { tone: 'error', icon: 'mdi-alert-outline', label: this.$t('identityIntegrations.certificate.invalid') as string };
  }

  openCreateDialog() {
    this.newName = '';
    this.newSlug = '';
    this.newProtocol = IDENTITY_INTEGRATION_PROTOCOL_SAML;
    this.createErrors = [];
    this.createDialog = true;
  }

  fieldError(field: string): string[] {
    return fieldErrorMessages(this, this.createErrors, field);
  }

  get createGeneralError(): string | null {
    return generalErrorMessage(this, this.createErrors, ['name', 'slug', 'protocol']);
  }

  async submitCreate() {
    this.creating = true;
    this.createErrors = [];
    try {
      const created = await identityIntegrationsService.create({
        name: this.newName,
        slug: this.newSlug,
        protocol: this.newProtocol,
      });
      this.createDialog = false;
      this.$uiBus.emit('info', this.$t('identityIntegrations.create.created') as string);
      this.$router.push({ name: 'identityIntegration', params: { integrationId: String(created.id) } });
    } catch (error) {
      this.createErrors = collectErrors(error);
    } finally {
      this.creating = false;
    }
  }

  beforeDestroy() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
  }
}
